import { Button, cn } from '@giyus/ui';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useId, useState } from 'react';

export interface MonthCalendarLabels {
  readonly previousMonth: string;
  readonly nextMonth: string;
  readonly previousYear: string;
  readonly nextYear: string;
  readonly today: string;
  readonly dayEvents: (date: string, count: number) => string;
}
export interface MonthCalendarProps {
  readonly locale: string;
  readonly labels: MonthCalendarLabels;
  readonly today?: Date;
  readonly selectedDate?: string;
  readonly onSelectDate: (date: string) => void;
  readonly eventCounts?: Readonly<Record<string, number>>;
}

export function calendarDate(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Full month, including leap days; local calendar dates never pass through UTC conversion. */
export function calendarDays(year: number, month: number): readonly Date[] {
  const offset = new Date(year, month, 1).getDay();
  const length = Math.ceil((offset + new Date(year, month + 1, 0).getDate()) / 7) * 7;
  return Array.from({ length }, (_, index) => new Date(year, month, index - offset + 1));
}

export function MonthCalendar({
  locale,
  labels,
  today = new Date(),
  selectedDate,
  onSelectDate,
  eventCounts = {},
}: MonthCalendarProps) {
  const headingId = useId();
  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const move = (months: number) => {
    setView(new Date(view.getFullYear(), view.getMonth() + months, 1));
  };
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const title = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(view);
  return (
    <section aria-labelledby={headingId} className="min-w-0 space-y-3">
      <div className="flex items-center justify-between gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={labels.previousYear}
          onClick={() => {
            move(-12);
          }}
        >
          <ChevronsLeft aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={labels.previousMonth}
          onClick={() => {
            move(-1);
          }}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <h3
          id={headingId}
          className="min-w-0 text-center text-body-sm font-semibold text-heading capitalize"
          aria-live="polite"
        >
          {title}
        </h3>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={labels.nextMonth}
          onClick={() => {
            move(1);
          }}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label={labels.nextYear}
          onClick={() => {
            move(12);
          }}
        >
          <ChevronsRight aria-hidden="true" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-caption">
        {Array.from({ length: 7 }, (_, day) => (
          <span key={day} className="py-1 text-muted-foreground">
            {weekday.format(new Date(2024, 0, 7 + day))}
          </span>
        ))}
        {calendarDays(view.getFullYear(), view.getMonth()).map((date) => {
          const iso = calendarDate(date);
          const count = eventCounts[iso] ?? 0;
          const isToday = iso === calendarDate(today);
          return (
            <Button
              key={iso}
              size="sm"
              variant={iso === selectedDate ? 'secondary' : 'ghost'}
              className={cn(
                'relative min-w-0 px-0 font-data',
                date.getMonth() !== view.getMonth() && 'text-muted-foreground',
                isToday && 'ring-1 ring-ring',
              )}
              aria-label={labels.dayEvents(dateFormat.format(date), count)}
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={iso === selectedDate}
              onClick={() => {
                onSelectDate(iso);
              }}
            >
              {date.getDate()}
              {count > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0.5 size-1 rounded-control bg-primary"
                />
              )}
            </Button>
          );
        })}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => {
          setView(new Date(today.getFullYear(), today.getMonth(), 1));
        }}
      >
        {labels.today}
      </Button>
    </section>
  );
}
